/* Dispatch deadline — one source of truth.
 *
 * The PDP reassurance bar and the cart drawer timer both count to the same moment. If each owned
 * its own copy of these rules, the two would eventually disagree by an hour or a day and the store
 * would be contradicting itself on the same journey. So the rules live here and nowhere else.
 *
 * THE RULES (see snippets/ft-reassurance-bar.liquid for why):
 *   Mon-Fri before the cutoff  -> ships today,     deadline = today's cutoff
 *   Mon-Thu after the cutoff   -> ships tomorrow,  deadline = MIDNIGHT tonight
 *   Fri after cutoff, Sat, Sun -> ships Monday,    deadline = a rolling 12h priority window
 *
 * The midnight deadline is true, not a device: an order placed tonight is in tomorrow's
 * pre-cutoff batch. The weekend window is anchored to midday and midnight rather than to page
 * load, so every visitor looking at the same clock time sees the same number and a refresh never
 * restarts it. That is the difference between urgency and a dark pattern, and it is the whole
 * reason this file exists rather than a `duration` countdown.
 */
window.FTDispatch = (function () {
  'use strict';

  function storeNow(tz) {
    try { return new Date(new Date().toLocaleString('en-US', { timeZone: tz })); }
    catch (e) { return new Date(); }
  }
  function at(now, h) { var t = new Date(now); t.setHours(h, 0, 0, 0); return t; }
  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  /* opts: { cutoffHour, tz, sameWeekDay, sameWeekHour } */
  function compute(opts) {
    var cut = parseInt(opts.cutoffHour, 10);
    if (isNaN(cut)) cut = 14;
    var now = storeNow(opts.tz || 'Australia/Brisbane');
    var dow = now.getDay();
    var todayCut = at(now, cut);
    var isWeekend = dow === 6 || dow === 0 || (dow === 5 && now >= todayCut);

    var phase, deadline;
    if (isWeekend) {
      phase = 'weekend';
      deadline = at(now, now.getHours() < 12 ? 12 : 24);   // rolling 12h, fixed anchors
    } else if (now < todayCut) {
      phase = 'today';
      deadline = todayCut;
    } else {
      phase = 'tomorrow';
      deadline = at(now, 24);
    }

    var ms = deadline - now;
    if (ms < 0) ms = 0;

    /* The day stock actually leaves. Expected arrival is counted from here, so it can never
       disagree with what the dispatch line above it says. */
    var dispatch = new Date(now);
    dispatch.setHours(0, 0, 0, 0);
    if (phase === 'tomorrow') dispatch.setDate(dispatch.getDate() + 1);
    else if (phase === 'weekend') {
      do { dispatch.setDate(dispatch.getDate() + 1); }
      while (dispatch.getDay() === 0 || dispatch.getDay() === 6);
    }

    /* the same-week arrival rule, so the cart and the PDP agree on that too */
    var swDay = parseInt(opts.sameWeekDay, 10); if (isNaN(swDay)) swDay = 3;
    var swHour = parseInt(opts.sameWeekHour, 10); if (isNaN(swHour)) swHour = 13;
    var d7 = dow || 7;
    var sameWeek = d7 < swDay ? true : (d7 > swDay ? false : now < at(now, swHour));

    return {
      now: now,
      phase: phase,                 // 'today' | 'tomorrow' | 'weekend'
      isWeekend: isWeekend,
      deadline: deadline,
      dispatch: dispatch,
      ms: ms,
      sameWeek: sameWeek,
      clock: pad(Math.floor(ms / 3600000)) + ':' +
             pad(Math.floor((ms % 3600000) / 60000)) + ':' +
             pad(Math.floor((ms % 60000) / 1000))
    };
  }

  /* Business days only — Aus Post does not deliver on a weekend, so counting calendar days
     would promise a Saturday arrival. */
  function addBusinessDays(from, n) {
    var t = new Date(from);
    while (n > 0) {
      t.setDate(t.getDate() + 1);
      if (t.getDay() !== 0 && t.getDay() !== 6) n--;
    }
    return t;
  }

  return { compute: compute, addBusinessDays: addBusinessDays };
})();
